import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { format, parse } from 'date-fns';

import Loading from "../../../components/Loading";
import Data from '../../../../repository';
import { createStudioSchema } from '../../../../validation/schemas';

import styles from '../../../../styles/Admin.module.scss';

function UpdateStudioForm() {
	const { id } = useParams();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [serverError, setServerError] = useState(null);
	const [sessionsError, setSessionsError] = useState(null);
	const [studio, setStudio] = useState(null);
	const [interval, setInterval] = useState('');
	const [sessions, setSessions] = useState([{
		day: 'Mon',
		start: '09:00',
		end: '17:00'
	}]);

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset
	} = useForm({
		resolver: yupResolver(createStudioSchema),
		defaultValues: {
			name: '',
			lock: '',
			capacity: 1,
			enabled: true
		}
	});

	useEffect(() => {
		fetchStudio();
	}, []);

	const fetchStudio = async () => {
		setLoading(true);
		try {
			const result = await Data.fetchStudio(id);
			setStudio(result.data);
			setInterval(result.data.interval ? result.data.interval.toString() : '');
			setSessions(result.data.sessions?.length ? result.data.sessions : [{
				day: 'Mon',
				start: '09:00',
				end: '17:00'
			}]);
			reset({
				name: result.data.name || '',
				lock: result.data.lock || '',
				capacity: result.data.capacity || 1,
				enabled: result.data.enabled
			});
		} catch (error) {
			setServerError('Failed to load studio');
		} finally {
			setLoading(false);
		}
	}

	const onSubmit = async (data) => {
		setSaving(true);
		setServerError(null);
		setSessionsError(null);

		const hasInvalidSession = sessions.some((session) => !session.day || !session.start || !session.end);
		if (!sessions.length || hasInvalidSession) {
			setSessionsError('Please add at least one session with day, start, and end times.');
			setSaving(false);
			return;
		}

		try {
			await Data.updateStudio({
				...studio,
				name: data.name,
				lock: data.lock,
				capacity: Number(data.capacity || 1),
				enabled: data.enabled === 'true' || data.enabled === true,
				interval: interval ? parseInt(interval) : null,
				sessions
			});
			navigate('/admin/studios');
		} catch (error) {
			setServerError(error.response?.data?.message || 'Failed to update studio. Please try again.');
			setSaving(false);
		}
	}

	const addSession = (event) => {
		event.preventDefault();
		setSessions((currentSessions) => ([
			...currentSessions,
			{ day: 'Mon', start: '09:00', end: '17:00' }
		]));
	}

	const removeSession = (index, event) => {
		event.preventDefault();
		setSessions((currentSessions) => currentSessions.filter((_, idx) => idx !== index));
	}

	const updateSession = (index, key, value) => {
		setSessions((currentSessions) => currentSessions.map((session, idx) => {
			if (idx !== index) return session;
			return { ...session, [key]: value };
		}));
	}

	if (loading) return <Loading button={false} />;

	return (
		<>
			<form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
				<div>
					<p>Name: *</p>
					<input
						type="text"
						{...register('name')}
						className={errors.name ? 'input-error' : ''}
					/>
					{errors.name && <span className="form-field-error">{errors.name.message}</span>}
				</div>

				<div>
					<p>Lock:</p>
					<input type="text" {...register('lock')} />
				</div>

				<div>
					<p>Capacity:</p>
					<input
						type="number"
						min="1"
						{...register('capacity')}
						className={errors.capacity ? 'input-error' : ''}
					/>
					{errors.capacity && <span className="form-field-error">{errors.capacity.message}</span>}
				</div>

			<div>
				<p>Status:</p>
				<select {...register('enabled')}>
					<option value="true">Enabled</option>
					<option value="false">Disabled</option>
				</select>
			</div>

			<div>
				<p>Interval (minutes):</p>
				<input
					type="number"
					min="5"
					max="480"
					placeholder="e.g. 60 for 1 hour slots"
					value={interval}
					onChange={(e) => setInterval(e.target.value)}
				/>
				<small style={{ color: '#666', display: 'block', gridColumn: '2' }}>
					Leave empty to use the full session time as one slot
				</small>
			</div>

			<h2>Sessions</h2>
			{sessions.map((session, idx) => {
				return (
					<div key={`${session.day}-${idx}`} className={styles.period}>
						<select value={session.day} onChange={(event) => updateSession(idx, 'day', event.target.value)}>
							<option value="Mon">Mon</option>
							<option value="Tue">Tue</option>
							<option value="Wed">Wed</option>
							<option value="Thu">Thu</option>
							<option value="Fri">Fri</option>
							<option value="Sat">Sat</option>
							<option value="Sun">Sun</option>
						</select>
						<TimePicker
							value={session.start ? parse(session.start, 'HH:mm', new Date()) : null}
							onChange={(date) => updateSession(idx, 'start', date ? format(date, 'HH:mm') : '')}
							ampm={false}
						/>
						<TimePicker
							value={session.end ? parse(session.end, 'HH:mm', new Date()) : null}
							onChange={(date) => updateSession(idx, 'end', date ? format(date, 'HH:mm') : '')}
							ampm={false}
						/>
						<img src={require('../../../../images/cross.svg').default} alt="Remove" onClick={(event) => removeSession(idx, event)} />
					</div>
				)
			})}
			<button className="btn btnEmpty" onClick={addSession}>+ Add Session</button>
			{sessionsError && <p style={{ color: 'red' }}>{sessionsError}</p>}

			{serverError && <p style={{ color: 'red' }}>{serverError}</p>}

			<button type="submit" className="btn btnPrimary" disabled={saving}>
				{saving ?
					<Loading button={true} />
						:
						`Save Changes`
					}
				</button>
			</form>

			<div className={`${styles.form} ${styles.withheld}`}>
				<h2>Withheld Periods</h2>
				{studio?.withheld?.map((group) => {
					return (
						<Link key={group._id} to={`withheld/${group._id}`} className={styles.group}>
							<p>{group.name}</p>
							<p>{group.sessions.length} Periods</p>
						</Link>
					)
				})}
				<Link to={`/admin/studios/${studio?._id}/withheld/create`} className="btn btnPrimary">Create Withheld Periods</Link>
			</div>
		</>
	)
}

export default UpdateStudioForm;
